/**
 * TweaksPanel — floating workspace appearance controls.
 *
 * Cycle-105.8 sprint-3 (S3, g231) · FR-W-4 (task 231.5). De-orphans
 * `useWorkspaceTweaks` (previously zero consumers) with pure consumer wiring —
 * the hook already validates/coerces field-by-field and syncs cross-tab, so no
 * hook changes are needed (SDD §4.1).
 *
 * Structurally mirrors the handoff
 * (`design_handoff_echelon_2026_04_26/workspace/tweaks.jsx`) but only surfaces
 * the controls that genuinely connect this cycle, so no control is inert
 * (FR-H-1 connect-or-delete):
 *   - MODULE STYLE → `.module-terminal` / `.module-palantir` (design-system.css)
 *   - PANEL FRAME  → `.panel-flat` / `.panel-notched` on the workspace root
 *   - Tag FORGE-derived → drives `ForgeDerivedBadge` visibility
 * The remaining `WorkspaceTweaks` fields stay in the hook (still valid,
 * cross-tab-synced) and will be surfaced as their consumers land.
 */

import { useState } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkspaceTweaks, type WorkspaceTweaks } from '../../hooks/useWorkspaceTweaks';
import { ForgeDerivedBadge } from './ForgeDerivedBadge';

export function TweaksPanel() {
  const { tweaks, setTweak } = useWorkspaceTweaks();
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="tweaks-panel-trigger"
        aria-label="Open workspace tweaks"
        className="fixed right-4 bottom-4 z-[100] flex items-center gap-1.5 rounded-md px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--e-text-secondary)] hover:text-[var(--e-text-primary)] bg-[var(--e-bg-card)]/90 backdrop-blur-md"
        style={{ border: '1px solid var(--e-border-secondary)' }}
      >
        <SlidersHorizontal className="w-3.5 h-3.5" /> Tweaks
      </button>
    );
  }

  return (
    <div
      data-testid="tweaks-panel"
      role="dialog"
      aria-label="Workspace tweaks"
      className="fixed right-4 bottom-4 z-[100] w-[280px] rounded-md overflow-hidden bg-[var(--e-bg-card)]/95 backdrop-blur-md shadow-xl"
      style={{ border: '1px solid var(--e-border-primary)' }}
    >
      <div
        className="flex items-center justify-between px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--e-text-muted)]"
        style={{ borderBottom: '1px solid var(--e-border-secondary)' }}
      >
        <span>Tweaks · workspace</span>
        <button onClick={() => setOpen(false)} aria-label="Close tweaks" className="hover:text-[var(--e-text-primary)]">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <Field label="Module style">
        <Seg
          value={tweaks.moduleStyle}
          options={[
            ['palantir', 'PALANTIR'],
            ['terminal', 'TERMINAL'],
          ]}
          onSelect={(v) => setTweak('moduleStyle', v as WorkspaceTweaks['moduleStyle'])}
        />
      </Field>

      <Field label="Panel frame">
        <Seg
          value={tweaks.panelStyle}
          options={[
            ['notched', 'NOTCHED'],
            ['flat', 'FLAT'],
          ]}
          onSelect={(v) => setTweak('panelStyle', v as WorkspaceTweaks['panelStyle'])}
        />
      </Field>

      <div className="px-3 py-2.5 flex flex-col gap-2.5">
        <label className="flex items-center justify-between text-[12px] text-[var(--e-text-secondary)] cursor-pointer">
          <span className="flex items-center gap-1.5">
            Tag FORGE-derived
            {tweaks.forgeDerivedTags && <ForgeDerivedBadge compact />}
          </span>
          <input
            type="checkbox"
            checked={tweaks.forgeDerivedTags}
            onChange={(e) => setTweak('forgeDerivedTags', e.target.checked)}
            aria-label="Tag FORGE-derived citations"
          />
        </label>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--e-border-secondary)' }}>
      <div className="font-mono text-[10px] tracking-[0.08em] text-[var(--e-text-muted)] mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function Seg({
  value,
  options,
  onSelect,
}: {
  value: string;
  options: Array<[string, string]>;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="flex rounded-sm overflow-hidden" style={{ border: '1px solid var(--e-border-primary)' }}>
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onSelect(v)}
          aria-pressed={value === v}
          className={cn(
            'flex-1 px-2 py-1.5 font-mono text-[10px] tracking-[0.06em] transition-colors',
            value === v
              ? 'bg-[var(--e-text-primary)] text-[var(--e-bg-card)]'
              : 'text-[var(--e-text-secondary)] hover:text-[var(--e-text-primary)]',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
